import type { JsonValue } from "@postdaily/api/lib/json";

/**
 * How to add PostDaily to each AI app, from one table. Formats come from each
 * client's docs and source (docs/mcp/research/spec-and-clients.md, IDE
 * addendum); re-check a client's entry there before changing its snippet.
 *
 * `oauth` apps sign in through PostDaily's consent page and need only the
 * server URL. `key` apps cannot do OAuth (or ignore headers once they see it,
 * like Cursor), so they use the key-only URL with an API key.
 */

export type ClientAuth = "oauth" | "key";

export type ClientGuide = {
  /** A second way to set it up, shown under the main one with its own copy button. */
  alternative?: { label: string; snippet: (urls: McpUrls) => string };
  auth: ClientAuth;
  /** One-click install link, when the app has one. */
  deeplink?: (urls: McpUrls) => string;
  deeplinkLabel?: string;
  id: string;
  name: string;
  /** Config file or command, when setup is not purely clicks. */
  snippet?: (urls: McpUrls) => string;
  snippetLabel?: string;
  steps: string[];
};

export type McpUrls = {
  /** Key-only endpoint: no OAuth discovery. */
  apiKeyUrl: string;
  /** OAuth (or API key) endpoint. */
  url: string;
};

const keyPlaceholder = "pd_live_YOUR_KEY";

function base64Json(value: Record<string, string>) {
  return btoa(JSON.stringify(value));
}

function json(value: JsonValue) {
  return JSON.stringify(value, null, 2);
}

export const clientGuides: ClientGuide[] = [
  {
    auth: "oauth",
    id: "claude",
    name: "Claude",
    steps: [
      "In Claude (web, desktop or mobile) open Customize → Connectors and select + next to Connectors.",
      "Find PostDaily in the directory and select Connect. Or choose + Add → Add custom connector, name it PostDaily and paste the server URL.",
      "Approve access in PostDaily.",
    ],
  },
  {
    auth: "oauth",
    id: "chatgpt",
    name: "ChatGPT",
    steps: [
      "In ChatGPT, open Settings → Security and login and turn on Developer mode.",
      "Open Plugins, select + and add PostDaily with the server URL under Connection.",
      "Sign in when ChatGPT asks, then approve access in PostDaily.",
    ],
  },
  {
    // The plugin (integrations/claude-code, published as a public repo)
    // always connects to production; the server-only command follows this
    // environment's URL.
    alternative: {
      label: "Or add just the server",
      snippet: ({ url }) => `claude mcp add --transport http postdaily ${url}`,
    },
    auth: "oauth",
    id: "claude-code",
    name: "Claude Code",
    snippet: () =>
      "claude plugin marketplace add mehedihasan2810/postdaily-claude-plugin\nclaude plugin install postdaily@postdaily",
    snippetLabel: "Terminal — PostDaily plugin",
    steps: [
      "Install PostDaily's plugin with the commands below. It adds the server, skills for uploading files from your computer and announcing releases, and asks before anything publishes.",
      "In Claude Code run /mcp, pick the postdaily server and sign in.",
    ],
  },
  {
    // The plugin (integrations/codex, published as a public repo) always
    // connects to production; the server-only commands follow this
    // environment's URL.
    alternative: {
      label: "Or add just the server",
      snippet: ({ url }) =>
        `codex mcp add postdaily --url ${url}\ncodex mcp login postdaily`,
    },
    auth: "oauth",
    id: "codex",
    name: "Codex",
    snippet: () =>
      "codex plugin marketplace add mehedihasan2810/postdaily-codex-plugin\ncodex plugin add postdaily@postdaily\ncodex mcp login postdaily",
    snippetLabel: "Terminal — PostDaily plugin",
    steps: [
      "Install PostDaily's plugin with the commands below. It adds the server and skills for uploading files from your computer and announcing releases, and Codex asks before anything publishes, is deleted or is canceled.",
      "The login opens PostDaily in your browser. The same setup works in the Codex IDE extension.",
    ],
  },
  {
    auth: "oauth",
    deeplink: ({ url }) =>
      `cursor://anysphere.cursor-deeplink/mcp/install?name=postdaily&config=${encodeURIComponent(base64Json({ url }))}`,
    deeplinkLabel: "Add to Cursor",
    id: "cursor",
    name: "Cursor",
    snippet: ({ url }) => json({ mcpServers: { postdaily: { url } } }),
    snippetLabel: "~/.cursor/mcp.json",
    steps: [
      "Use Add to Cursor, or add the config below to ~/.cursor/mcp.json.",
      "In Cursor Settings → Customize → MCPs, select Authenticate next to postdaily.",
      "With an API key instead, use the key-only URL and an Authorization header — Cursor ignores headers on the main URL.",
    ],
  },
  {
    auth: "oauth",
    deeplink: ({ url }) =>
      `vscode:mcp/install?${encodeURIComponent(JSON.stringify({ name: "postdaily", type: "http", url }))}`,
    deeplinkLabel: "Add to VS Code",
    id: "vscode",
    name: "VS Code",
    snippet: ({ url }) =>
      json({ servers: { postdaily: { type: "http", url } } }),
    snippetLabel: ".vscode/mcp.json",
    steps: [
      "Use Add to VS Code, or run MCP: Add Server and paste the server URL.",
      "Start the server from the MCP view and sign in when the browser opens.",
    ],
  },
  {
    auth: "oauth",
    id: "gemini",
    name: "Gemini CLI",
    snippet: ({ url }) => `gemini mcp add --transport http postdaily ${url}`,
    snippetLabel: "Terminal",
    steps: [
      "Run the command below.",
      "Inside Gemini CLI run /mcp auth postdaily to sign in.",
    ],
  },
  {
    auth: "oauth",
    id: "devin",
    name: "Devin / Windsurf",
    snippet: ({ url }) =>
      json({ mcpServers: { postdaily: { serverUrl: url } } }),
    snippetLabel: "mcp_config.json",
    steps: [
      "Open Cascade → MCP servers → View raw config and add the entry below (note serverUrl, not url).",
      "Refresh the servers list and sign in to PostDaily.",
    ],
  },
  {
    auth: "oauth",
    id: "zed",
    name: "Zed",
    snippet: ({ url }) => json({ context_servers: { postdaily: { url } } }),
    snippetLabel: "settings.json",
    steps: [
      "Open Settings → AI → MCP Servers → Add Remote Server, or add the entry below.",
      "Zed opens PostDaily to sign in.",
    ],
  },
  {
    auth: "oauth",
    deeplink: ({ url }) =>
      `goose://extension?${new URLSearchParams({ id: "postdaily", name: "PostDaily", timeout: "300", type: "streamable_http", url }).toString()}`,
    deeplinkLabel: "Add to Goose",
    id: "goose",
    name: "Goose",
    steps: [
      "Use Add to Goose, or Extensions → Add custom extension → Remote (Streamable HTTP) with the server URL.",
      "Goose opens PostDaily to sign in.",
    ],
  },
  {
    auth: "oauth",
    deeplink: ({ url }) =>
      `lmstudio://add_mcp?name=postdaily&config=${encodeURIComponent(base64Json({ url }))}`,
    deeplinkLabel: "Add to LM Studio",
    id: "lmstudio",
    name: "LM Studio",
    snippet: ({ url }) => json({ mcpServers: { postdaily: { url } } }),
    snippetLabel: "mcp.json",
    steps: [
      "Use Add to LM Studio, or Program → Install → Edit mcp.json and add the entry below.",
      "LM Studio opens PostDaily to sign in.",
    ],
  },
  {
    auth: "key",
    id: "cline",
    name: "Cline",
    snippet: ({ apiKeyUrl }) =>
      json({
        mcpServers: {
          postdaily: {
            headers: { Authorization: `Bearer ${keyPlaceholder}` },
            type: "streamableHttp",
            url: apiKeyUrl,
          },
        },
      }),
    snippetLabel: "cline_mcp_settings.json",
    steps: [
      "Create an API key below.",
      "Add the entry below in MCP Servers → Configure. Keep type streamableHttp — without it Cline uses the old SSE transport.",
    ],
  },
  {
    auth: "key",
    id: "jetbrains",
    name: "JetBrains AI",
    snippet: ({ apiKeyUrl }) =>
      json({
        mcpServers: {
          postdaily: {
            args: [
              "-y",
              "mcp-remote",
              apiKeyUrl,
              "--header",
              `Authorization: Bearer ${keyPlaceholder}`,
            ],
            command: "npx",
          },
        },
      }),
    snippetLabel: "AI Assistant → MCP",
    steps: [
      "JetBrains AI Assistant cannot sign in to remote servers yet, so it connects through mcp-remote with an API key.",
      "Create an API key below and add the entry in Settings → Tools → AI Assistant → Model Context Protocol.",
    ],
  },
  {
    auth: "key",
    id: "automation",
    name: "n8n, Make & scripts",
    snippet: ({ apiKeyUrl }) =>
      `URL:     ${apiKeyUrl}\nHeader:  Authorization: Bearer ${keyPlaceholder}\nTransport: Streamable HTTP`,
    snippetLabel: "MCP client node",
    steps: [
      "Create an API key below, scoped to the workspaces the automation needs.",
      "Point your MCP client node or SDK at the key-only URL with the key as a bearer token.",
    ],
  },
];
